import type {AssetsType} from '@packages/systems/designer/assets';
import type {
  FileRecord,
  FileRecordType,
  RawFileType,
} from '@packages/systems/core/records/FileRecord';
import {type StyleBlock} from '@packages/systems/style-legacy/records';
import {emptyArray, emptyObject, identity} from '@packages/utilities/fp/utils';
import {List, Map} from 'immutable';
import {
  getObject,
  getString,
  getArray,
  type Json,
} from '@packages/utilities/json';
import {weakMemo} from '@packages/utilities/memo';

import {
  ElementPreset,
  Plugins,
  Expressions,
  Component,
  matchExpression,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {findByFileHash} from '@packages/shared/utils/AssetUtils';
import {isLottieAsset, isRiveAsset} from '@packages/domains/assets/utilities';

import {
  typeName,
  namesEqual,
  qualify,
  type TypeName,
  type Qualified,
  type PluginName,
  getPluginName,
  getName,
} from '@packages/systems/wfdl-Qualified';
import {
  EElement,
  EText,
  ELiteral,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {CssVariablesStateType} from '@packages/systems/core/utils/StyleTypes';
import {
  replaceStyleVariablesInStyleBlock,
  VariableCollectionId,
  VariableCollectionMap,
} from '@packages/systems/core/utils/StyleVariables';
import {
  getLibrariesVariablesOriginIdMap,
  updateAssetIdsInExpression,
} from '@packages/systems/libraries';

import {
  buildStyleBlock,
  updateCssPropertyMap,
  sortByDepth,
  accumulateStyleBlocks,
  replaceBackgroundImages,
} from './StyleBlockUtils';
import {StyleBlockStateType} from '../states/StyleBlockStateTypes';
import StyleBlockState from '../states/StyleBlockState';
import {replaceCollectionIdsInModeApplicationsForBlock} from '@packages/systems/core/utils/StyleVariableModesCssUtils';

type ImportStylesFromPresetPayload = {
  elementPresetType?: ElementPreset.ElementPresetType;
  pluginName?: PluginName;
  initialStyleBlockId: string;
  styleBlockState: StyleBlockStateType;
  assetsToImport?: AssetsType;
  assets?: AssetsType;
  elementPresetImportStrategy?: Plugins.PluginImportStrategy;
  plugins: Plugins.PluginsType;
  variables?: CssVariablesStateType['variables'];
  variableCollections: CssVariablesStateType['variableCollections'];
  skipCssPropertyMapUpdate?: boolean;
  isOptimizeLibrariesPerformanceEnabled?: boolean;
};
type AssetUpdateDetails = {
  element: ExpressionElement;
  siteId: string;
  assetMap: {
    [key: string]: string;
  };
  ASSET_PATH: ExpressionPath;
  ASSET_TYPE_NAME: Qualified<TypeName>;
};

export const createFileHashToAssetIdMap = (files: Array<RawFileType>) => {
  return files.reduce<Record<string, string>>((acc, {fileHash, _id}) => {
    acc[fileHash] = _id;
    return acc;
  }, {});
};

const getStyleBlocksFromPreset = (
  elementPresetType?: ElementPreset.ElementPresetType | null,
  pluginName?: PluginName | null
) => {
  if (!elementPresetType || !pluginName) {
    return emptyArray;
  }

  return ElementPreset.getStyleBlocks(elementPresetType, pluginName);
};

const noStripFonts = (styleBlock: StyleBlock): [string[], StyleBlock] => [
  [],
  styleBlock,
];

const getAssetId = (asset: FileRecordType) => {
  // The Animation::Animation element (used to render Lottie assets) does not references
  // the asset ID directly, like Images or Videos, but instead references the asset's CDN URL.
  // When building the assetMap we need to account for this case, since the assetMap will
  // eventually be used to remap asset references in the element's data, which in the case of
  // Lottie assets will be the asset's CDN URL, not the ID.
  if (asset.get('type') === 'LOTTIE' || asset.get('type') === 'RIVE') {
    return asset.get('cdnUrl');
  }
  return asset.get('_id');
};

export const reconstructAssetMap = (
  assets: AssetsType | null | undefined,
  assetsToImport: AssetsType | null | undefined,
  elementPreset: ElementPreset.ElementPresetType | undefined,
  pluginName: string | undefined,
  importStrategy: Plugins.PluginImportStrategy | undefined,
  plugins: Plugins.PluginsType
): Map<string, string> => {
  // First let's build an assetMap with the assets to import
  const importedAssets = assetsToImport
    ? assetsToImport.map(getAssetId)
    : Map<string, string>();

  // Then let's look at the assets from the element preset
  if (!elementPreset) {
    return importedAssets;
  }
  const importByValue = importStrategy === Plugins.IMPORT_STRATEGY.VALUE;
  const existingAssets: {[id: string]: string} = {};
  const elementPresetAssets =
    Plugins.getElementPresetAssets(plugins, elementPreset) ?? [];
  elementPresetAssets.forEach((asset) => {
    // When importing by VALUE we tag the assets with their origin
    const origin =
      importByValue && pluginName ? qualify(pluginName, asset._id) : undefined;
    const existing = assets ? findByFileHash({...asset, origin})(assets) : null;
    if (existing) {
      existingAssets[asset._id] =
        // The Animation::Animation element (used to render Lottie assets) does not references
        // the asset ID directly, like Images or Videos, but instead references the asset's CDN URL.
        // When building the assetMap we need to account for this case, since the assetMap will
        // eventually be used to remap asset references in the element's data, which in the case of
        // Lottie assets will be the asset's CDN URL, not the ID.
        isLottieAsset(existing as FileRecord) ||
        isRiveAsset(existing as FileRecord)
          ? existing.cdnUrl
          : existing._id;
    }
  });

  return importedAssets.merge(existingAssets);
};

const IMAGE_TYPE_NAME = qualify(Plugins.name('Asset'), typeName('Image'));
const VIDEO_TYPE_NAME = qualify(Plugins.name('Embed'), typeName('Video'));
const IMAGE_TYPE = qualify(Plugins.name('Basic'), Component.name('Image'));
const LOTTIE_TYPE = qualify(
  Plugins.name('Animation'),
  Component.name('Animation')
);
const RIVE_TYPE = qualify(Plugins.name('Animation'), Component.name('Rive'));
const VIDEO_TYPE = qualify(Plugins.name('Basic'), Component.name('Video'));
const IMAGE_PATH = [{in: 'Record', at: 'img'}];
const VIDEO_PATH = [{in: 'Record', at: 'video'}];
const ASSET_SRC_PATH = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'src'},
] satisfies ExpressionPath;

export const updateAsset = ({
  element,
  siteId,
  assetMap,
  ASSET_PATH,
  ASSET_TYPE_NAME,
}: AssetUpdateDetails) => {
  let elementData = Expressions.getElementData(element);
  const assetData = Expressions.getIn(elementData, ASSET_PATH);
  if (!assetData) return EElement(element);
  const literalValue = Expressions.getLiteral(assetData);
  let oldAssetId = '';
  let sizes: Array<Json> = [];
  if (literalValue) {
    const obj = getObject(literalValue.value);
    // @ts-expect-error - TS2345 - Argument of type 'Json | undefined' is not assignable to parameter of type 'Json'.
    const str = obj ? getString(obj.id) : '';
    // @ts-expect-error - TS2345 - Argument of type 'Json | undefined' is not assignable to parameter of type 'Json'.
    const list = obj ? getArray(obj.sizes) : [];
    oldAssetId = str || '';
    sizes = list || [];
  }

  elementData = Expressions.updateIn(elementData, ASSET_SRC_PATH, (e) => {
    if (!Expressions.getText(e)) return e;

    const originalUrl = Expressions.getText(e) ?? '';

    /*
     * The originalUrl is in this format:
     * https://dev-assets.website-files.com/633397b2d99cdf0f62e2998b/633397c0d99cdf0f62e29992_lotus-flower.jpeg
     * To match on the site id we need to capture the second match group between two paranthesis: 633397b2d99cdf0f62e2998b
     */
    let oldSiteId = originalUrl.match(/.*\/(.*)\//);
    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'RegExpMatchArray'. | TS2554 - Expected 2 arguments, but got 1.
    oldSiteId = oldSiteId ? oldSiteId[1].replace(siteId) : '';

    const updatedUrl = originalUrl
      // @ts-expect-error - TS2769 - No overload matches this call.
      .replace(oldSiteId, siteId)
      // @ts-expect-error - TS2769 - No overload matches this call.
      .replace(oldAssetId, assetMap[oldAssetId]);

    return EText(updatedUrl);
  });

  elementData = Expressions.updateIn(elementData, ASSET_PATH, (_) => {
    return ELiteral({
      name: ASSET_TYPE_NAME,
      // @ts-expect-error - TS2322 - Type '{ id: string | undefined; sizes: Json[]; }' is not assignable to type 'Json'.
      value: {id: assetMap[oldAssetId], sizes},
    });
  });

  const updatedElement = Expressions.setElementData(element, elementData);
  return EElement(updatedElement);
};

function getCollectionIdMap(
  variableCollections: VariableCollectionMap,
  pluginName: string | undefined
) {
  const collectionIdMap: Record<VariableCollectionId, VariableCollectionId> =
    {};
  for (const [id, variableCollection] of variableCollections) {
    if (
      variableCollection.origin == null ||
      getPluginName(variableCollection.origin) !== pluginName
    )
      continue;
    collectionIdMap[getName(variableCollection.origin)] = id;
  }
  return collectionIdMap;
}

const replaceModeApplications =
  (collectionIdMap: Record<VariableCollectionId, VariableCollectionId>) =>
  (block: StyleBlock): StyleBlock =>
    replaceCollectionIdsInModeApplicationsForBlock(block, collectionIdMap);

export const updateElementAssetsFromPreset = (
  expression: ExpressionElement,
  siteId: string,
  assetMap: {
    [key: string]: string;
  }
): ExpressionElement => {
  const updatedElement = Expressions.everywhere(
    matchExpression({
      Element: (el) => {
        if (namesEqual(el.type, IMAGE_TYPE)) {
          return updateAsset({
            element: el,
            siteId,
            assetMap,
            // @ts-expect-error - TS2322 - Type '{ in: string; at: string; }[]' is not assignable to type 'ExpressionPath'.
            ASSET_PATH: IMAGE_PATH,
            ASSET_TYPE_NAME: IMAGE_TYPE_NAME,
          });
        }

        if (namesEqual(el.type, VIDEO_TYPE)) {
          return updateAsset({
            element: el,
            siteId,
            assetMap,
            // @ts-expect-error - TS2322 - Type '{ in: string; at: string; }[]' is not assignable to type 'ExpressionPath'.
            ASSET_PATH: VIDEO_PATH,
            ASSET_TYPE_NAME: VIDEO_TYPE_NAME,
          });
        }

        if (namesEqual(el.type, LOTTIE_TYPE)) {
          return updateAssetIdsInExpression(EElement(el), assetMap);
        }

        if (namesEqual(el.type, RIVE_TYPE)) {
          return updateAssetIdsInExpression(EElement(el), assetMap);
        }

        return EElement(el);
      },
      _: identity,
    })
  )(EElement(expression));

  return Expressions.getElement(updatedElement) || expression;
};

export const importNodeAddedStylesFromElementPreset = weakMemo(
  ({
    elementPresetType,
    pluginName,
    initialStyleBlockId,
    styleBlockState,
    assetsToImport,
    assets,
    elementPresetImportStrategy,
    plugins,
    variables,
    variableCollections,
    skipCssPropertyMapUpdate,
    isOptimizeLibrariesPerformanceEnabled,
  }: ImportStylesFromPresetPayload): {
    state: StyleBlockStateType;
    renames: {
      from: string;
      to: string;
    }[];
    droppedFonts: string[];
    nextId: string;
    idMap: {
      [key: string]: string;
    };
    parentIdMap: {
      [key: string]: string;
    };
  } => {
    const slugMap = Plugins.getPluginsSlugMap(plugins);
    const assetMap = reconstructAssetMap(
      assets,
      assetsToImport,
      elementPresetType,
      pluginName,
      elementPresetImportStrategy,
      plugins
    );

    let state = styleBlockState;
    /* If there are assets to import, we want to use the existing styleBlocks and see if they have
     * any assets that need to be updated. This will only be true when the ELEMENT_PRESET_ASSET_IMPORT_SUCCEEDED
     * action is triggered. The styleBlocks associated with the element preset will have been imported and created during
     * the ELEMENT_ADDED action. The only thing we want to do in this case is update the assetIds for an element preset's
     * background assets.
     */
    if (assetsToImport) {
      let styleBlocks = styleBlockState.get('styleBlocks', Map());
      styleBlocks = styleBlocks.map(replaceBackgroundImages(assetMap));
      state = new StyleBlockState(styleBlockState).set(
        'styleBlocks',
        styleBlocks
      );
    }

    // Only when we're importing by value do we want to provide `originPlugin`,
    // which is used to produce `origin` fields for any `StyleBlock`s that are
    // copied from the plugin by value.
    const originPlugin =
      elementPresetImportStrategy === Plugins.IMPORT_STRATEGY.VALUE
        ? pluginName
        : undefined;

    const variablesOriginIdMap =
      elementPresetImportStrategy === Plugins.IMPORT_STRATEGY.VALUE &&
      variables &&
      pluginName
        ? getLibrariesVariablesOriginIdMap(variables)(pluginName)
        : {};

    const collectionIdMap = getCollectionIdMap(variableCollections, pluginName);

    const results = List(
      getStyleBlocksFromPreset(elementPresetType, pluginName)
    )
      .map(buildStyleBlock)
      .map(replaceBackgroundImages(assetMap))
      .map(replaceModeApplications(collectionIdMap))
      .map(replaceStyleVariablesInStyleBlock(variablesOriginIdMap))
      .update(sortByDepth)
      .map(noStripFonts)
      .reduce(accumulateStyleBlocks, {
        importStrategy: 'DEFAULT',
        state,
        enforceUniqueComboClasses: false,
        renames: emptyArray,
        droppedFonts: emptyArray,
        nextId: initialStyleBlockId,
        idMap: emptyObject,
        parentIdMap: emptyObject,
        originPlugin,
        slugMap,
        isOptimizeLibrariesPerformanceEnabled,
      });

    // Let's only update cssPropertyMap if the state has change
    // otherwise let's return the same state by reference
    const hasStateChanged = results.state !== state;
    return !skipCssPropertyMapUpdate && hasStateChanged
      ? updateCssPropertyMap(results)
      : {
          state: results.state,
          renames: results.renames,
          droppedFonts: results.droppedFonts,
          nextId: results.nextId,
          idMap: results.idMap,
          parentIdMap: results.parentIdMap,
        };
  }
);

export const forTestSuite = {importNodeAddedStylesFromElementPreset} as const;
